import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { AssistantDraft } from '@/api/schemas/assistant';
import type { AssistantScope } from '@/modules/assistant/services/assistant-scope';

/**
 * ---------------------------------------------------------------------------
 * WHY A STORE AND NOT THE URL
 * ---------------------------------------------------------------------------
 * The selected conversation has to survive a reload. The two obvious homes for it
 * are the URL and a store, and the URL is the wrong one here:
 *
 *   - every page route would have to declare the assistant's search param in its
 *     own `validateSearch`, or the param would be dropped on the next navigation,
 *     and the panel's state would leak into the contract of every page that has
 *     nothing to do with the assistant; and
 *   - the selected conversation is not a property of the PAGE. It is a property of
 *     the person talking to the assistant, and it survives the page changing.
 *
 * So the selection lives in a store, and `persist` puts it in `localStorage` — which
 * is what makes it survive a reload. The conversation's CONTENTS are not persisted
 * here: the server owns the history, and the panel reads the thread list from the
 * API on mount.
 */
export type AssistantSelectionState = {
  /** The open conversation, or null when the person is starting a new one. */
  threadId: string | null;
  /** The family the conversation belongs to. */
  scope: AssistantScope | null;
  /** Opens a conversation, or clears the selection with null. */
  selectThread: (threadId: string | null, scope: AssistantScope) => void;
};

export const useAssistantSelectionStore = create<AssistantSelectionState>()(
  persist(
    (set) => ({
      threadId: null,
      scope: null,
      selectThread: (threadId, scope) => set({ threadId, scope }),
    }),
    {
      name: 'mana-locals.assistant.selection',
      // Only the two values are persisted. The action is a function, and a stored
      // function is not a thing a reload can restore.
      partialize: (state) => ({ threadId: state.threadId, scope: state.scope }),
    },
  ),
);

/**
 * ---------------------------------------------------------------------------
 * THE DRAFT STORE — A HAND-OVER, NOT A WRITE
 * ---------------------------------------------------------------------------
 * The assistant produces a draft: proposed values for a form. The panel publishes
 * the chosen draft here, and a form page that can accept it subscribes and fills
 * its own state.
 *
 * The panel never applies the draft itself. There is deliberately no write in this
 * module: the person reviews the proposed values in the panel, chooses to apply
 * them, and the ORDINARY form performs the ordinary write, with the ordinary
 * validation. A model that could write would be a model that could write wrongly.
 *
 * The draft is NOT persisted. A draft published before a reload is a proposal about
 * a form that may no longer be on screen, and re-applying it silently would be a
 * change the person never asked for.
 */
export type AssistantDraftState = {
  /** The published draft, or null when there is none. */
  draft: AssistantDraft | null;
  /** Publishes a draft for a form to pick up. */
  setDraft: (draft: AssistantDraft) => void;
  /** Withdraws the published draft. */
  clearDraft: () => void;
};

export const useAssistantDraftStore = create<AssistantDraftState>()((set) => ({
  draft: null,
  setDraft: (draft) => set({ draft }),
  clearDraft: () => set({ draft: null }),
}));
